/**
 * ShowValuesContext.jsx — Phase 19.3.
 *
 * Global "show numeric values" toggle. Single source of truth for the
 * CascadeNetwork + DistributionBar + DistributionTimeline + ConsequenceCard
 * + HorizonCompare + RiskMatrix. Persisted in localStorage under
 * `coda.showValues` so the preference survives a hard reload.
 *
 * Usage:
 *   <ShowValuesProvider>
 *     <PageThatRendersVisuals />
 *   </ShowValuesProvider>
 *
 *   const { showValues, toggle, setShowValues } = useShowValues();
 *
 * Isolated component use (e.g. Storybook) — pass an explicit boolean:
 *   const { showValues } = useShowValues(true);
 *
 * The hook always returns a well-shaped object so consumers don't need
 * to guard against missing providers.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const LS_KEY = "coda.showValues";
const TOGGLE_EVENT = "coda:show-values-toggle";
const ShowValuesContext = createContext(null);

/**
 * Ask the page-level provider to flip the toggle (issue #247). For callers
 * mounted outside the provider, such as the app-level command palette,
 * which used to click a test id that no longer exists. Same open-contract
 * shape as `lib/guideOpen.js`. Returns false when there is no window.
 */
export function requestShowValuesToggle() {
    if (typeof window === "undefined") return false;
    window.dispatchEvent(new CustomEvent(TOGGLE_EVENT));
    return true;
}

function readLS() {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(LS_KEY) === "1"; }
    catch (_) { return false; }
}
function writeLS(v) {
    if (typeof window === "undefined") return;
    try { window.localStorage.setItem(LS_KEY, v ? "1" : "0"); }
    catch (_) { /* noop */ }
}

export function ShowValuesProvider({ children }) {
    // Issue #247 — a provider nested inside another (CascadesTab mounts
    // one so it also works standalone) used to start its own state from localStorage, so the
    // page toggle never reached those tabs live. Inside a provider, pass
    // straight through and share the outer state.
    const outer = useContext(ShowValuesContext);
    if (outer) return children;
    return <RootShowValuesProvider>{children}</RootShowValuesProvider>;
}

function RootShowValuesProvider({ children }) {
    const [showValues, setShowValues] = useState(readLS);
    useEffect(() => { writeLS(showValues); }, [showValues]);
    const toggle = useCallback(() => setShowValues((v) => !v), []);
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        window.addEventListener(TOGGLE_EVENT, toggle);
        return () => window.removeEventListener(TOGGLE_EVENT, toggle);
    }, [toggle]);
    return (
        <ShowValuesContext.Provider value={{ showValues, setShowValues, toggle }}>
            {children}
        </ShowValuesContext.Provider>
    );
}

/**
 * useShowValues([override])
 *
 * - When `override` is a boolean → return a read-only view fixed at that
 *   value (isolated component use, tests, Storybook).
 * - When a Provider is mounted → return its live state + toggle.
 * - When neither → return the persisted localStorage value as a read-only
 *   snapshot so the component still shows the user's preference.
 */
export function useShowValues(override) {
    const ctx = useContext(ShowValuesContext);
    if (typeof override === "boolean") {
        return { showValues: override, setShowValues: () => {}, toggle: () => {} };
    }
    if (ctx) return ctx;
    return { showValues: readLS(), setShowValues: () => {}, toggle: () => {} };
}
